import React, { useEffect, useReducer } from "react";
import "./ChangeLog.scss";
import { FixedHeader, PrimaryHeader } from "../../ui/Header";
import DateRange from "../../ui/DateRange/DateRange";
import Select from "../../ui/Select/Select";
import ClosableLabel from "../../ui/ClosableLabel/ClosableLabel";
import useThis from "../../hooks/useThis";
import useSyncSearch from "../../hooks/useSyncSearch";
import InfiniteScroll from "../../components/InfiniteScroll";
import Loader from "../../ui/Loader/Loader";

import reducer, { getInitialState } from "./reducer";

import { ChangeLogListService } from "../../utils/api";

import {
  changeLogActionOptions,
  changeLogActionTypeOptions,
  categoryGroupOptions,
  userTypeOptions
} from "../../constants";

import ChangeLogTable from "./ChangeLogTable";
import LogContext from "./LogContext";
import UserSuggest from "./UserSuggest";

import moment from "moment";
import { ItemsCount } from "./ItemsCount";

const TeasersLog = ({ location, history }) => {
  const [state, dispatch] = useReducer(
    reducer,
    location.search,
    getInitialState
  );

  const search = state.filters;
  const log = state.history;

  const onDateChange = ({ startDate, endDate }) => {
    setSearch({
      date_from: moment(startDate).format("YYYY-MM-DD"),
      date_to: moment(endDate).format("YYYY-MM-DD")
    });
  };

  const onActionSelect = selected => {
    setSearch({ action: selected.value });
  };

  const onActionTypeSelect = selected => {
    setSearch({ action_type: selected.value });
  };

  const onUserSelect = selected => {
    setSearch({ uid: selected.value });
  };

  const onGroupSelect = selected => {
    setSearch({ category_group: selected.value });
  };

  const onUserTypeSelect = selected => {
    setSearch({ user_type: selected.value });
  };

  const clearTid = () => {
    setSearch({ tid: "" });
  };

  const onSortChange = ({ sortBy, sortOrder }) => {
    setSearch({ sort_by: sortBy, sort_order: sortOrder });
  };

  // action creators
  const setSearch = search => {
    dispatch({ type: "SET_SEARCH", search });
  };

  const setLoading = loading => {
    dispatch({ type: "SET_LOADING", loading });
  };

  const setLog = data => {
    dispatch({ type: "SET_LOG", data });
  };

  const appendLog = data => {
    dispatch({ type: "APPEND_LOG", data });
  };

  const setPage = page => {
    dispatch({ type: "SET_PAGE", page });
  };

  const incPage = () => {
    dispatch({ type: "INC_PAGE" });
  };

  const setHasMore = hasMore => {
    dispatch({ type: "SET_HAS_MORE", hasMore });
  };

  const load = (search, page) => {
    setLoading(true);

    const params = Object.assign(search, { page });

    ChangeLogListService(params).then(response => {
      if (page == 1) {
        setLog({
          items: response.items,
          campaigns: response.campaigns,
          categories: response.categories,
          item_cnt: response.item_cnt
        });
      } else {
        appendLog({
          items: response.items,
          campaigns: response.campaigns,
          categories: response.categories,
          item_cnt: response.item_cnt
        });
      }
      if (response.items.length == 0) {
        setHasMore(false);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    load(state.filters, state.meta.page);
  }, [state.filters, state.meta.page]);

  useSyncSearch(history, location, [[state.filters, ""]]);

  const thisLoading = useThis(state.meta.loading);
  const thisHasMore = useThis(state.meta.hasMore);

  const onNextLoad = () => {
    if (thisLoading.current) {
      return;
    }

    if (!thisHasMore.current) {
      return;
    }

    setLoading(true);
    incPage();
  };

  return (
    <div className="change-log">
      <FixedHeader>
        <PrimaryHeader>
          <span className="header__title">Changelog</span>
        </PrimaryHeader>
      </FixedHeader>
      <div className="content-body content-body__with-fixed">
        <div className="change-log__filters">
          <Select
            placeholder="Действие"
            options={changeLogActionOptions}
            onSelect={onActionSelect}
            value={search.action}
          />

          <Select
            placeholder="Тип действия"
            options={changeLogActionTypeOptions}
            onSelect={onActionTypeSelect}
            value={parseInt(search.action_type)}
          />

          <UserSuggest
            placeholder="Пользователь"
            onSelect={onUserSelect}
            value={parseInt(search.uid)}
          />

          <Select
            placeholder="Тип пользователя"
            options={userTypeOptions}
            onSelect={onUserTypeSelect}
            value={search.user_type}
          />

          <Select
            placeholder="Тип категории"
            options={categoryGroupOptions}
            onSelect={onGroupSelect}
            value={parseInt(search.category_group)}
          />

          {search.tid && (
            <ClosableLabel onClose={clearTid}>tid: {search.tid}</ClosableLabel>
          )}

          <ItemsCount count={log?.item_cnt || 0} />

          <DateRange
            className="left"
            startDate={moment(search.date_from)}
            endDate={moment(search.date_to)}
            onChange={onDateChange}
          />
        </div>
        <LogContext.Provider
          value={{
            categories: log.categories,
            campaigns: log.campaigns
          }}
        >
          <InfiniteScroll onNextLoad={onNextLoad}>
            <ChangeLogTable
              items={log.items}
              onSortChange={onSortChange}
              sortBy={search.sort_by}
              sortOrder={search.sort_order}
              loading={state.meta.isFirstLoading}
            />
            {!state.meta.isFirstLoading && state.meta.loading && <Loader />}
          </InfiniteScroll>
        </LogContext.Provider>
      </div>
    </div>
  );
};

export default TeasersLog;
